import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { Modal, Backdrop, Fade, IconButton } from "@material-ui/core";
import SearchBar from "../../common/SearchBar";
import useSettings from "../../../hooks/useSettings";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { CustomDatePicker } from "../../common/FormInputs";
import moment from "moment";

export default function CaseLifeCycleStatus({
  lifeCycle,
  setLifeCycle,
  status = [],
  setStatus,
}) {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [inputText, setInputText] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const { caseLifeCycleStatus = [] } = useSettings();

  useEffect(() => {
    if (Array.isArray(status) && status.length > 0) {
      setSelectedItems(
        status.map((item) => ({
          ...item,
          date: item.date ? new Date(item.date) : new Date(),
        }))
      );
    } else if (status && status.value) {
      setSelectedItems([
        {
          label: status.label,
          value: status.value,
          date: status.date ? new Date(status.date) : new Date(),
        },
      ]);
    } else {
      setSelectedItems([]);
    }
  }, [status]);

  const filteredStatuses = useMemo(() => {
    if (!inputText.trim()) return caseLifeCycleStatus;
    return caseLifeCycleStatus.filter((item) =>
      item.label.toLowerCase().includes(inputText.toLowerCase())
    );
  }, [caseLifeCycleStatus, inputText]);

  const toggleStatus = (item) => {
    setSelectedItems((prev) => {
      const exists = prev.find((s) => s.value === item.value);
      if (exists) {
        return prev.filter((s) => s.value !== item.value);
      }
      return [...prev, { ...item, date: new Date() }];
    });
  };

  const updateDate = (newDate, itemValue) => {
    setSelectedItems((prev) =>
      prev.map((s) => (s.value === itemValue ? { ...s, date: newDate } : s))
    );
  };

  const removeStatus = (value) => {
    setSelectedItems((prev) => prev.filter((s) => s.value !== value));
  };

  const confirmAndSave = async () => {
    try {
      setLoader({ state: true, message: "Updating case status..." });
      await new Promise((res) => setTimeout(res, 800));

      setStatus(selectedItems);
      enqueueSnackbar(
        `${selectedItems.length} status(es) updated successfully!`,
        { variant: "success" }
      );
      setLifeCycle(false);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <Modal
      open={lifeCycle}
      onClose={() => setLifeCycle(false)}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={lifeCycle}>
        <Container>
          <Header>
            <CloseBtn onClick={() => setLifeCycle(false)}>
              <img
                src={require("../../../assets/images/closeModal.svg")}
                alt="close"
              />
            </CloseBtn>
          </Header>
          <SearchContainer>
            <SearchBar
              name={"caseManger"}
              placeHolderText={"Search Sub Stages"}
              backgroundColor="#f9f9f9"
              {...{ inputText, setInputText }}
              width="90%"
            />
          </SearchContainer>

          <ContentWrapper>
            <ListContainer>
              {filteredStatuses.length === 0 ? (
                <EmptyState>No status found</EmptyState>
              ) : (
                filteredStatuses.map((item) => {
                  const isSelected = selectedItems.some(
                    (s) => s.value === item.value
                  );
                  return (
                    <StatusRow
                      key={item.value}
                      selected={isSelected}
                      onClick={() => toggleStatus(item)}
                    >
                      <Left>
                        <Checkbox checked={isSelected}>
                          {isSelected && <CheckMark>✓</CheckMark>}
                        </Checkbox>
                        <Label>{item.label}</Label>
                      </Left>
                    </StatusRow>
                  );
                })
              )}
            </ListContainer>
            {selectedItems.length > 0 && (
              <SelectedSection>
                <SectionTitle>
                  Selected Updates ({selectedItems.length})
                </SectionTitle>
                {selectedItems.map((item) => (
                  <SelectedItem key={item.value}>
                    <ItemInfo>
                      <div>{item.label}</div>
                      <RemoveBtn onClick={() => removeStatus(item.value)}>
                        ×
                      </RemoveBtn>
                    </ItemInfo>

                    <CustomDatePicker
                      className="input-white"
                      inputVariant="outlined"
                      maxDate={new Date()}
                      format="DD/MM/YYYY"
                      value={item.date}
                      onChange={(date) => {
                        const newDate = moment(date).format("YYYY-MM-DD");
                        const timePart = moment(item.date).format("HH:mm");
                        updateDate(
                          moment(
                            `${newDate} ${timePart}`,
                            "YYYY-MM-DD HH:mm"
                          ).toDate(),
                          item.value
                        );
                      }}
                      style={{ marginTop: 6 }}
                    />
                  </SelectedItem>
                ))}
              </SelectedSection>
            )}
          </ContentWrapper>
          <Footer>
            <ClearBtn
              onClick={() => setSelectedItems([])}
              disabled={selectedItems.length === 0}
            >
              Clear All
            </ClearBtn>
            <ActionButtons>
              <CancelBtn onClick={() => setLifeCycle(false)}>Cancel</CancelBtn>
              <SaveBtn
                disabled={selectedItems.length === 0}
                onClick={confirmAndSave}
              >
                Save Updates ({selectedItems.length})
              </SaveBtn>
            </ActionButtons>
          </Footer>
        </Container>
      </Fade>
    </Modal>
  );
}

const Container = styled.div`
  max-width: 560px;
  width: 94%;
  max-height: 92vh;
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  color: white;
  position: relative;
  flex-shrink: 0;
`;

const CloseBtn = styled(IconButton)`
  position: absolute !important;
  right: 12px;
  top: 12px;
  color: white !important;
`;

const SearchContainer = styled.div`
  padding: 16px 32px;
  background: #f8f9fa;
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ListContainer = styled.div`
  padding: 12px 32px;
  flex-shrink: 0;
  max-height: 320px;
  overflow-y: auto;
  border-bottom: 1px solid #eee;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 12px;
  border-radius: 12px;
  margin: 6px 0;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ selected }) => (selected ? "#e1e3ee" : "transparent")};

  &:hover {
    background: ${({ selected }) => (selected ? "#e1e3ee" : "#f8f9fa")};
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

const Checkbox = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 2px solid ${({ checked }) => (checked ? "#00838c" : "#ccc")};
  background: ${({ checked }) => (checked ? "#00838c" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckMark = styled.span`
  color: white;
  font-size: 14px;
  font-weight: bold;
`;

const Label = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
  color: ${COLORS.COLOR_DARK};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
`;

const SelectedSection = styled.div`
  padding: 20px 32px;
  background: #f0f9ff;
  flex-shrink: 0;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px;
  color: #1e40af;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SelectedItem = styled.div`
  background: white;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ItemInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  color: #949ab0;
  font-family: ${theme.fonts.primaryFontSemiBold};
  strong {
    font-size: 15px;
  }
`;

const RemoveBtn = styled.button`
  background: #fee2e2;
  color: #ef4444;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  font-weight: bold;
`;

const Footer = styled.div`
  padding: 20px 32px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
`;

const ClearBtn = styled.button`
  background: transparent;
  border: none;
  color: #d91b1b;
  font-family: ${theme.fonts.primaryFontSemiBold};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const CancelBtn = styled.button`
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  background: #d91b1b;
  color: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const SaveBtn = styled.button`
  padding: 10px 24px;
  background: #00838c;
  color: white;
  font-family: ${theme.fonts.primaryFontSemiBold};
  border: none;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;
